<po-page-default p-title="API Keys" [p-actions]="pageActions">
  <p class="api-keys-description">
    Uma API Key permite que um sistema, script ou integração use o ESP sem
    depender do login de uma pessoa. Ela age com as permissões da Conta de
    serviço da chave, e sempre as permissões do momento: se a conta perde um
    acesso, a chave perde junto. Ao criar, você recebe dois valores &mdash; o
    <strong>Client ID</strong>, que pode ser consultado aqui quando quiser, e o
    <strong>Client Secret</strong>, que aparece uma única vez e não pode ser
    recuperado depois.
  </p>

  <p class="api-keys-description" *ngIf="!managesOnlySharedKeys">
    A lista abaixo traz as API Keys que você gerencia.
  </p>

  <p class="api-keys-description" *ngIf="managesOnlySharedKeys">
    A lista abaixo traz as API Keys que outras contas de serviço permitiram você
    gerenciar.
  </p>

  <po-container *ngIf="errorMessage" class="api-keys-notice">
    <po-info
      p-label="Não foi possível carregar"
      [p-value]="errorMessage"
    ></po-info>
  </po-container>

  <div
    class="api-keys-callout api-keys-callout-warning"
    *ngIf="showActiveLimitWarning"
  >
    <span class="an an-warning api-keys-callout-icon" aria-hidden="true"></span>

    <div>
      <p class="po-font-text-large-bold api-keys-callout-title">
        Limite de API Keys ativas atingido
      </p>
      <p
        class="po-font-text api-keys-callout-text"
        *ngIf="hasReachedActiveLimit"
      >
        {{ canCreateMessage }}
      </p>
      <p
        class="po-font-text api-keys-callout-text"
        [class.api-keys-callout-stacked]="hasReachedActiveLimit"
        *ngIf="hasLimitReachedOwners"
      >
        {{ limitReachedOwnersMessage }}
      </p>
    </div>
  </div>

  <po-table
    class="po-mb-1"
    [p-columns]="columns"
    [p-items]="items"
    [p-actions]="tableActions"
    [p-loading]="loadingList || loadingRevoke"
    [p-striped]="true"
    p-sort="true"
    p-actions-right="true"
    p-hide-columns-manager="true"
    [p-literals]="tableLiterals"
  >
  </po-table>

  <po-divider class="po-m-2" p-label="Como usar a sua API Key"></po-divider>

  <p class="po-font-text api-keys-examples-intro">
    Em toda chamada ao ESP, envie o Client ID e o Client Secret juntos, nos
    cabeçalhos <code>X-Client-Id</code> e <code>X-Client-Secret</code>. Os dois
    são obrigatórios. Escolha abaixo o formato mais próximo do seu caso e troque
    <code>cole-aqui-o-client-id</code> e
    <code>cole-aqui-o-client-secret</code> pelos valores da sua chave. Os
    endereços são fictícios: use a URL da API que você vai chamar.
  </p>

  <po-tabs>
    <po-tab
      *ngFor="let example of usageExamples; let first = first"
      [p-label]="example.label"
      [p-active]="first"
    >
      <div class="api-keys-example">
        <p class="po-font-text-small api-keys-example-hint">
          {{ example.hint }}
        </p>

        <div class="api-keys-example-editor">
          <po-code-editor
            [ngModel]="example.code"
            [p-language]="example.language"
            [p-readonly]="true"
            [p-show-diff]="false"
            p-height="280"
            p-theme="vs"
          >
          </po-code-editor>

          <po-button
            class="api-keys-example-copy"
            p-kind="primary"
            p-size="small"
            [attr.title]="
              copiedExampleId === example.id ? 'Copiado' : 'Copiar exemplo'
            "
            [p-aria-label]="
              copiedExampleId === example.id
                ? 'Exemplo ' + example.label + ' copiado'
                : 'Copiar exemplo ' + example.label
            "
            [p-icon]="
              copiedExampleId === example.id
                ? 'po-icon po-icon-ok'
                : 'po-icon po-icon-copy'
            "
            (p-click)="copyExample(example)"
          >
          </po-button>
        </div>
      </div>
    </po-tab>
  </po-tabs>
</po-page-default>

<po-modal
  #createModal
  p-title="Criar API Key"
  [p-primary-action]="createModalPrimaryAction"
  [p-secondary-action]="createModalSecondaryAction"
  [p-hide-close]="loadingCreate"
  (p-close)="onCreateModalClose()"
>

  <div class="api-keys-create-body">

    <div class="po-row" *ngIf="showOwnerPicker">
      <po-select
        class="po-md-12"
        name="apiKeyOwner"
        p-label="Conta de serviço"
        p-placeholder="Escolha a conta"
        p-help="A chave vai agir com as permissões desta conta. Aparecem aqui as contas que adicionaram você na gerência de acessos."
        p-required="true"
        [p-options]="createOwnerOptions"
        [ngModel]="newKeyOwner"
        [p-disabled]="loadingCreate"
        (ngModelChange)="onNewKeyOwnerChange($event)"
      >
      </po-select>
    </div>

    <div class="po-row" *ngIf="!showOwnerPicker && newKeyOwner">
      <po-input
        class="po-md-12"
        name="apiKeyOwnerFixed"
        p-label="Conta de serviço"
        p-help="A chave vai agir com as permissões desta conta."
        p-readonly="true"
        [ngModel]="newKeyOwner"
      >
      </po-input>
    </div>

    <div class="po-row">
      <po-input
        #apiKeyNameInput
        class="po-md-12 po-mt-2"
        name="apiKeyName"
        p-label="Nome"
        p-placeholder="Digite um nome"
        p-help="Use um nome que lembre onde a chave vai ser usada. Ex.: MCP, Agente de IA, Integração CI, etc."
        p-maxlength="80"
        p-required="true"
        p-clean="true"
        [ngModel]="newKeyName"
        [p-disabled]="loadingCreate"
        (ngModelChange)="onNewKeyNameChange($event)"
        (p-enter)="create()"
      >
      </po-input>
    </div>

    <po-loading-overlay
      *ngIf="loadingCreate"
      p-size="sm"
      p-text="Criando API Key"
    >
    </po-loading-overlay>
  </div>
</po-modal>

<po-modal
  #revealModal
  p-title="API Key criada"
  p-size="lg"
  p-icon="po-icon po-icon-lock"
  [p-primary-action]="revealModalPrimaryAction"
  (p-close)="onRevealModalClose()"
>
  <div class="api-keys-callout api-keys-callout-warning">
    <span class="an an-warning api-keys-callout-icon" aria-hidden="true"></span>

    <div>
      <p class="po-font-text-large-bold api-keys-callout-title">
        Copie o Client Secret antes de fechar
      </p>
      <p class="po-font-text api-keys-callout-text">
        Esta é a única vez que o Client Secret aparece. Ao fechar esta janela
        ele não pode mais ser consultado, nem por você nem pelo time do ESP. Se
        isso acontecer, revogue esta chave e crie outra.
      </p>
    </div>
  </div>

  <po-divider class="po-mb-1" p-label="Credenciais"></po-divider>

  <div class="po-row api-keys-credential">
    <po-input
      class="po-md-9"
      name="createdClientId"
      p-label="Client ID"
      p-icon="po-icon po-icon-user"
      p-help="Vai no cabeçalho X-Client-Id. Não é secreto e você pode consultá-lo depois, na lista de chaves."
      p-readonly="true"
      [ngModel]="createdClientId || ''"
    >
    </po-input>

    <po-button
      class="po-md-3 po-pl-1 po-pt-4"
      p-aria-label="Copiar Client ID"
      [p-label]="copiedClientId ? 'Copiado' : 'Copiar'"
      [p-icon]="copiedClientId ? 'po-icon po-icon-ok' : 'po-icon po-icon-copy'"
      [p-disabled]="!createdClientId"
      (p-click)="copyClientId()"
    >
    </po-button>
  </div>

  <div class="po-row api-keys-credential">
    <po-password
      class="po-md-9"
      name="createdClientSecret"
      p-label="Client Secret"
      p-help="Vai no cabeçalho X-Client-Secret. Guarde num gerenciador de senhas ou de segredos, nunca no código."
      p-readonly="true"
      p-no-autocomplete="true"
      [ngModel]="createdClientSecret || ''"
    >
    </po-password>

    <po-button
      class="po-md-3 po-pl-1 po-pt-4"
      p-kind="primary"
      p-aria-label="Copiar Client Secret"
      [p-label]="copiedClientSecret ? 'Copiado' : 'Copiar'"
      [p-icon]="
        copiedClientSecret ? 'po-icon po-icon-ok' : 'po-icon po-icon-copy'
      "
      [p-disabled]="!createdClientSecret"
      (p-click)="copyClientSecret()"
    >
    </po-button>
  </div>

  <div class="api-keys-callout api-keys-callout-info">
    <span class="an an-info api-keys-callout-icon" aria-hidden="true"></span>

    <p class="po-font-text api-keys-callout-text">
      Quem usar esta chave age como
      <strong>{{ createdOwner || currentUsername }}</strong> e com os mesmos
      acessos dessa conta. Compartilhe apenas com quem pode agir no lugar dela.
    </p>
  </div>
</po-modal>

<po-modal
  #revokeModal
  p-title="Revogar API Key"
  p-icon="an an-warning"
  [p-primary-action]="revokeModalPrimaryAction"
  [p-secondary-action]="revokeModalSecondaryAction"
  [p-hide-close]="loadingRevoke"
  (p-close)="onRevokeModalClose()"
>
  <div class="api-keys-revoke-body" *ngIf="revokeTarget">
    <div class="api-keys-callout api-keys-callout-warning">
      <span
        class="an an-warning api-keys-callout-icon"
        aria-hidden="true"
      ></span>

      <p class="po-font-text api-keys-callout-text">
        A chave para de funcionar imediatamente e
        <strong>não pode ser reativada</strong>. Tudo o que estiver usando ela
        deixa de acessar o ESP.
      </p>
    </div>

    <po-container class="api-keys-revoke-target">
      <div class="po-row">
        <po-info class="po-md-4" p-label="Chave" [p-value]="revokeTarget.name">
        </po-info>

        <po-info
          class="po-md-4"
          p-label="Client ID"
          [p-value]="revokeTarget.clientId"
        >
        </po-info>

        <po-info
          class="po-md-4"
          p-label="Conta de serviço"
          [p-value]="revokeTarget.owner || currentUsername"
        >
        </po-info>
      </div>
    </po-container>

    <po-input
      name="revokeConfirmation"
      p-label="Digite o nome da chave"
      p-help="A revogação só é liberada quando o nome digitado for exatamente igual ao da chave acima, inclusive maiúsculas e minúsculas."
      p-clean="true"
      p-placeholder="Digite a chave"
      p-no-autocomplete="true"
      [p-icon]="revokeConfirmationIcon"
      [ngModel]="revokeConfirmation"
      [p-disabled]="loadingRevoke"
      (ngModelChange)="onRevokeConfirmationChange($event)"
    >
    </po-input>

    <po-loading-overlay
      *ngIf="loadingRevoke"
      p-size="sm"
      p-text="Revogando API Key"
    >
    </po-loading-overlay>
  </div>
</po-modal>

<po-modal
  #accessModal
  p-title="Quem pode gerenciar as suas API Keys"
  p-size="lg"
  (p-close)="onAccessModalClose()"
>
  <p class="api-keys-description">
    Quem você adicionar aqui vai poder
    <strong>ver, criar e revogar</strong> as suas API Keys &mdash; e somente as
    suas. As chaves continuam sendo de <strong>{{ currentUsername }}</strong
    >, inclusive as que essa pessoa criar, e continuam agindo com os seus
    acessos. Por isso, adicione apenas quem pode agir no seu lugar.
  </p>

  <p class="po-font-text-small api-keys-access-rule">
    Adicione quem <strong>não tem permissão de API Keys</strong>. Uma conta que
    pode ter chave própria não pode gerenciar as chaves de outra, porque isso
    faria a posse de um Client Secret valer como acesso a esta conta.
  </p>

  <po-container>
    <div class="po-row api-keys-access-form">
      <po-input
        class="po-md-9"
        name="accessUsername"
        p-icon="po-icon po-icon-user"
        p-label="Dar acesso a"
        p-placeholder="nome.sobrenome ou nome.sobrenome@totvs.com.br"
        p-help="Informe o nome de usuário ou o e-mail corporativo da pessoa."
        p-clean="true"
        p-maxlength="120"
        [(ngModel)]="accessUsername"
        [p-disabled]="loadingAccess"
        (p-enter)="grantAccess()"
      >
      </po-input>

      <po-button
        class="po-md-3 po-pl-1 po-pt-4"
        p-label="Conceder"
        p-kind="primary"
        [p-disabled]="!accessUsername.trim()"
        [p-loading]="loadingAccess"
        (p-click)="grantAccess()"
      >
      </po-button>
    </div>
  </po-container>

  <po-divider class="po-m-1" p-label="Quem tem acesso hoje"></po-divider>

  <po-table
    [p-columns]="accessColumns"
    [p-items]="accessHolders"
    [p-actions]="accessActions"
    [p-loading]="loadingAccess"
    [p-striped]="true"
    p-sort="true"
    p-actions-right="true"
    p-hide-columns-manager="true"
  >
  </po-table>

  <po-modal-footer>
    <po-button
      p-label="Exportar CSV"
      p-icon="an an-file-csv"
      [p-disabled]="loadingAccess || !accessHolders.length"
      (p-click)="exportAccessCsv()"
    >
    </po-button>

    <po-button p-label="Fechar" p-kind="primary" (p-click)="closeAccessModal()">
    </po-button>
  </po-modal-footer>
</po-modal>
